import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Get language from localStorage
  const language = localStorage.getItem('language') || localStorage.getItem('selectedLanguage') || 'lv';

  const translations = {
    title: {
      en: 'Forgot Password',
      ru: 'Забыли пароль',
      lv: 'Aizmirsi paroli'
    },
    subtitle: {
      en: 'Enter your email and we\'ll send you a reset link',
      ru: 'Введите свой email, и мы отправим вам ссылку для сброса',
      lv: 'Ievadiet savu e-pastu, un mēs nosūtīsim jums atiestatīšanas saiti'
    },
    email: {
      en: 'Email Address',
      ru: 'Адрес электронной почты',
      lv: 'E-pasta adrese'
    },
    sendButton: {
      en: 'Send Reset Link',
      ru: 'Отправить ссылку',
      lv: 'Nosūtīt atiestatīšanas saiti'
    },
    backToLogin: {
      en: 'Back to Login',
      ru: 'Вернуться к входу',
      lv: 'Atpakaļ uz pieteikšanos'
    },
    success: {
      en: 'If your email is registered, you will receive a password reset link shortly. Please check your inbox.',
      ru: 'Если ваш email зарегистрирован, вы скоро получите ссылку для сброса пароля. Проверьте свою почту.',
      lv: 'Ja jūsu e-pasts ir reģistrēts, drīz saņemsiet paroles atiestatīšanas saiti. Lūdzu, pārbaudiet savu iesūtni.'
    },
    successTitle: {
      en: 'Email Sent!',
      ru: 'Email отправлен!',
      lv: 'E-pasts nosūtīts!'
    },
    checkSpam: {
      en: 'Didn\'t receive the email? Check your spam folder.',
      ru: 'Не получили письмо? Проверьте папку спам.',
      lv: 'Nesaņēmāt e-pastu? Pārbaudiet surogātpasta mapi.'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Password reset email sent');
    } catch (error) {
      // Even on error, show success message to prevent email enumeration
      setSent(true);
      console.error('Forgot password error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rz-page rz-form min-h-screen flex items-center justify-center p-5">
        <div className="w-full max-w-md">
          <Button
            variant="ghost" style={{minHeight:44}}
            onClick={() => navigate('/login')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {translations.backToLogin[language]}
          </Button>

          <div className="bg-white border border-[#E7E5E4] p-8 sm:p-10">
            {/* Italian flag logo */}
            <p className="rz-eyebrow text-center m-0 mb-5">Roze di Napoli</p>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#F5F2EC] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#1C1917]" />
              </div>
              <h1 className="font-display text-[2rem] leading-tight text-[#1C1917] mb-3">{translations.successTitle[language]}</h1>
              <p className="text-[#57534E] mb-4">{translations.success[language]}</p>
              <p className="text-sm text-[#78716C] mb-6">{translations.checkSpam[language]}</p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full rz-btn !rounded-none"
              >
                {translations.backToLogin[language]}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rz-page rz-form min-h-screen flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Button
          variant="ghost" style={{minHeight:44}}
          onClick={() => navigate('/login')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {translations.backToLogin[language]}
        </Button>

        <div className="bg-white border border-[#E7E5E4] p-8 sm:p-10">
          {/* Italian flag logo */}
          <p className="rz-eyebrow text-center m-0 mb-5">Roze di Napoli</p>

          <h1 className="font-display text-[2rem] leading-tight text-[#1C1917] text-center mb-3">{translations.title[language]}</h1>
          <p className="text-center text-[#57534E] mb-6">{translations.subtitle[language]}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="w-4 h-4 inline mr-2" />
                {translations.email[language]}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
              />
            </div>

            <Button
              type="submit"
              className="w-full rz-btn !rounded-none"
              disabled={loading}
            >
              {loading ? '...' : translations.sendButton[language]}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center min-h-[44px] text-sm text-[#57534E] hover:text-[#1C1917]"
            >
              {translations.backToLogin[language]}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
